import { Card, Table } from "@jsq/ds/core";
import { ComplianceObjectType } from "~/graphql/supergraphEnums";
import {
  getOwnershipPercentageStr,
  getProfileNameForProfileType,
  getRelationships,
  getTitle,
} from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { formatShortFormDate } from "~/utils/datetime";
import { capitalizeEachWord } from "~/utils/strings";
import { AddressViewOnly } from "../../../../common/AddressViewOnly";
import { ProfileDetailsType } from "../../../types";
import { TableDetailRow } from "../../TableDetailRow";

interface Props {
  complianceProfile: ProfileDetailsType | null | undefined;
  caseId: number;
}

export const ViewProfileDetailsCard = ({ complianceProfile, caseId }: Props) => {
  const titleName = complianceProfile && getProfileNameForProfileType(complianceProfile);
  const profileType = complianceProfile?.complianceObjectType;
  const isInvestingProfile = complianceProfile?.complianceCases.filter(
    (ccp) => ccp.complianceCase?.id === caseId,
  )[0]?.isInvestingProfile;
  const dateOfBirth =
    complianceProfile?.dateOfBirth && formatShortFormDate(complianceProfile.dateOfBirth);
  const dateOfFormation =
    complianceProfile?.dateOfOrganization &&
    formatShortFormDate(complianceProfile.dateOfOrganization);
  const relationships = getRelationships(complianceProfile?.parentRelationships);

  const ownershipPercentageStr =
    relationships === "Owner" ||
    relationships === "Owner and Controller" ||
    (relationships === "Owner, Controller, and Payment beneficiary" &&
      getOwnershipPercentageStr(complianceProfile?.parentRelationships));

  const titleStr =
    relationships === "Controller" ||
    relationships === "Owner and Controller" ||
    (relationships === "Owner, Controller, and Payment beneficiary" &&
      getTitle(complianceProfile?.parentRelationships));

  const address = complianceProfile?.addresses.find((x) => x.isPrimaryDomicile);

  const dataSet = [
    { label: "Type", value: capitalizeEachWord(complianceProfile?.complianceObjectType) },
    !isInvestingProfile && { label: "Role(s)", value: relationships },
    ownershipPercentageStr &&
      !isInvestingProfile && {
        label: "Ownership percentage",
        value: getOwnershipPercentageStr(complianceProfile?.parentRelationships),
      },
    { label: "Full legal name", value: titleName },
    titleStr && {
      label: "Title",
      value: getTitle(complianceProfile?.parentRelationships) ?? "--",
    },
    profileType === ComplianceObjectType.Individual && {
      label: "Date of birth",
      value: dateOfBirth,
    },
    profileType === ComplianceObjectType.Institution && {
      label: "Date of formation",
      value: dateOfFormation,
    },
    {
      label: complianceProfile?.taxIdType?.toUpperCase() ?? "Tax ID",
      value: complianceProfile?.taxIdUnencrypted,
    },
  ].filter(Boolean);

  return (
    <Card title={titleName ?? ""}>
      <Table variant="muted" condensed>
        {dataSet
          .filter(
            (row): row is { label: string; value: string } =>
              typeof row === "object" && row !== null && "label" in row && "value" in row,
          )
          .map((row, index) => (
            <TableDetailRow key={index} label={row.label} value={String(row.value ?? "--")} />
          ))}
        <AddressViewOnly
          hasAddress={!!address}
          street={address?.street ?? ""}
          street1={address?.street2 ?? ""}
          city={address?.city ?? ""}
          zipcode={address?.postalCode ?? ""}
          country={address?.country?.name ?? ""}
          stateCode={address?.state?.code ?? null}
          province={address?.province ?? null}
        />
      </Table>
    </Card>
  );
};
